/**
 * Player Page
 */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import PlayerBanner from "../components/players/PlayerBanner";
import Container from "../components/core/Container";

import TeammatesSidebar from "../components/players/TeammatesSidebar";
import AchievementsPanel from "../components/players/AchievementsPanel";
import DetailsPanel from "../components/players/DetailsPanel";

import StatsRow from "../components/players/StatsRow";
import { Helmet } from "react-helmet-async";

const playerHeaders = ["Season", "Team", "GP", "G", "A", "PTS", "PIM"];

const goalieHeaders = [
  { name: "Season", extraClasses: "" },
  { name: "Team", extraClasses: "" },
  { name: "GP", extraClasses: "" },
  { name: "MINS", extraClasses: "max-xs:hidden" },
  { name: "GA", extraClasses: "max-xs:hidden" },
  { name: "SA", extraClasses: "max-xs:hidden" },
  { name: "SAV%", extraClasses: "" },
  { name: "GAA", extraClasses: "" },
  { name: "SO", extraClasses: "" },
];

const processPlayerData = (jsonData) => {
  // SKATER TOTALS
  jsonData.gp = 0;
  jsonData.g = 0;
  jsonData.a = 0;
  jsonData.pims = 0;
  jsonData.points = 0;
  jsonData.pp_goals = 0;
  jsonData.sh_goals = 0;
  jsonData.gw_goals = 0;

  // ADD SKATER TOTALS - iterate jsonData.stats and get stats from each, add them together
  jsonData.stats.forEach((stat) => {
    jsonData.gp += stat.games;
    jsonData.g += stat.goals;
    jsonData.a += stat.assists;
    jsonData.pims += stat.pims;
    jsonData.points += stat.goals + stat.assists;
    jsonData.pp_goals += stat.pp_goals;
    jsonData.sh_goals += stat.sh_goals;
    jsonData.gw_goals += stat.gw_goals;
  });

  // NETMINDER TOTALS
  jsonData.seconds = 0;
  jsonData.shots_against = 0;
  jsonData.goals_against = 0;
  jsonData.wins = 0;
  jsonData.losses = 0;
  jsonData.shutouts = 0;

  // ADD NETMINDER TOTALS - iterate jsonData.nm_stats and get stats from each, add them together
  jsonData.nm_stats.forEach((stat) => {
    jsonData.seconds += stat.seconds;
    jsonData.shots_against += stat.shots_against;
    jsonData.goals_against += stat.goals_against;
    jsonData.wins += stat.wins;
    jsonData.losses += stat.losses;
    jsonData.shutouts += stat.shutouts;
  });

  // NETMINDER CALCULATIONS
  if (jsonData.shots_against > 0) {
    // SAV%
    jsonData.sav_pct = 1 - jsonData.goals_against / jsonData.shots_against;
  }
  if (jsonData.seconds > 0) {
    // GAA
    jsonData.gaa = (jsonData.goals_against * 3600) / jsonData.seconds;
  }

  // NATIONALITY
  jsonData.nationality = [];
  if (jsonData.nat !== "") jsonData.nationality.push(jsonData.nat);
  if (
    jsonData.nat_dual !== "" &&
    jsonData.nat_dual !== undefined &&
    jsonData.nat_dual !== null
  )
    jsonData.nationality.push(jsonData.nat_dual);

  return jsonData;
};

const getPlayerStatus = (status) => {

  switch (status) {
    case 1:
      return "Active";
    case 2:
      return "Pending";
    case 3:
      // Competition Reason
      return "Suspended";
    case 4:
      // Registration Reason
      return "Suspended";
    default:
      return "Retired";
  }
}
const SinglePlayer = () => {

  const [isMobile, setIsMobile] = useState(window.innerWidth < 400);

  useEffect(() => {
    window.addEventListener("resize", () => {
      const ismobile = window.innerWidth < 400;
      if (ismobile !== isMobile) setIsMobile(ismobile);
    });
  }, [isMobile]);

  // Get player ID from URL
  const { playerId } = useParams();
  const [data, setData] = useState(undefined);

  // Fetch player data from API
  useEffect(() => {
    setData(undefined);

    fetch(`${process.env.REACT_APP_BUIHA_API_URL}/player/${playerId}/full/`)
      .then((response) => response.json())
      .then((json) => {
        let data = processPlayerData(json);

        setData(data);
      })
      .catch((error) => console.error(error));
  }, [playerId]);

  // Gradient style using primary team colours
  const gradientStyle = {
    background: `linear-gradient(to right, ${
      data ? data.primary_team.col1 : "#ffffff"
    }, ${data ? data.primary_team.col2 : "#ffffff"})`,
  };

  // Valid Player
  return data ? (
    <>
      <Helmet>
        <title>
          {data.forename} {data.surname} - Player Profile
          | BUIHA
        </title>
        <meta
          name="description"
          content={`BUIHA Hockey Player profile for ${data.forename} ${
            data.surname
          }, playing for ${
            data.primary_team.name
          } - Player Profile`}
        />
        <link rel="canonical" href={`https://buiha.org/player/${playerId}`} />
        <link
          rel="icon"
          type="image/png"
          href={`https://buiha.org.uk/assets/img/icon/60/${data.primary_team.logo}.png`}
        />
      </Helmet>

      <PlayerBanner
        id={data.id}
        forename={data.forename}
        surname={data.surname}
        club_id={data.primary_team.club}
        club_logo={data.primary_team.logo}
        col1={data.primary_team.col1}
        col2={data.primary_team.col2}
        institution={
          data.registrations.length > 0
            ? data.registrations[data.registrations.length - 1].uni
            : ""
        }
        mainteam={data.primary_team.name}
        joined={
          data.registrations.length > 0 ? data.registrations[0].season : ""
        }
        status={getPlayerStatus(data.active)}
        regType={
          data.registrations.length > 0
            ? data.registrations[data.registrations.length - 1].stat
            : ""
        }
        number={data.profile.number ? data.profile.number : null}
        nationality={data.nationality}
      />

      <Container
        children={
          <div className="bg-light-1 dark:bg-dark-1">
            {/* Player Header Stats - Based on Netminder or Not TODO move to own component */}
            <div className="flex flex-row justify-center items-center pt-2">
              {/* GP */}
              <div className="w-24 xxs:w-28 flex-col justify-center items-center mx-2 xs:mx-3 sm:mx-6">
                <p className="text-xs font-semibold xxs:text-lg xs:text-xl text-center dark-text leading-tight">
                  {isMobile ? 'GP' : 'Games'}
                </p>

                <div className="w-full h-1 xs:h-2 mt-1 xxs:mt-1" style={gradientStyle}>
                  <p className="hidden">Gradient line</p>
                </div>
                <p className="text-2xl md:text-3xl text-center mt-1 leading-tight">
                  {data.gp}
                </p>
              </div>

              {/* GOALS */}
              {!data.is_netminder && (
                <div className="w-24 xxs:w-28 flex-col justify-center items-center mx-2 xs:mx-3 sm:mx-6">
                  <p className="text-xs font-semibold xxs:text-lg xs:text-xl text-center dark-text leading-tight">
                  {isMobile ? 'G' : 'Goals'}
                  </p>

                  <div className="w-full h-1 xs:h-2 mt-1 xxs:mt-1" style={gradientStyle}>
                    <p className="hidden">Gradient line</p>
                  </div>
                  <p className="text-2xl md:text-3xl text-center mt-1 leading-tight">
                    {data.g}
                  </p>
                </div>
              )}

              {/* ASSISTS */}
              {!data.is_netminder && (
                <div className="w-24 xxs:w-28 flex-col justify-center items-center mx-2 xs:mx-3 sm:mx-6">
                  <p className="text-xs font-semibold xxs:text-lg xs:text-xl text-center dark-text leading-tight">
                  {isMobile ? 'A' : 'Assists'}
                  </p>

                  <div className="w-full h-1 xs:h-2 mt-1 xxs:mt-1" style={gradientStyle}>
                    <p className="hidden">Gradient line</p>
                  </div>
                  <p className="text-2xl md:text-3xl text-center mt-1 leading-tight">
                    {data.a}
                  </p>
                </div>
              )}

              {/* POINTS */}
              {!data.is_netminder && (
                <div className="w-24 xxs:w-28 flex-col justify-center items-center mx-2 xs:mx-3 sm:mx-6">
                  <p className="text-xs font-semibold xxs:text-lg xs:text-xl text-center dark-text leading-tight">
                  {isMobile ? 'PTS' : 'Points'}
                  </p>

                  <div className="w-full h-1 xs:h-2 mt-1 xxs:mt-1" style={gradientStyle}>
                    <p className="hidden">Gradient line</p>
                  </div>
                  <p className="text-2xl md:text-3xl text-center mt-1 leading-tight">
                    {data.points}
                  </p>
                </div>
              )}

              {/* PIMS */}
              {!data.is_netminder && (
                <div className="w-24 xxs:w-28 flex-col justify-center items-center mx-2 xs:mx-3 sm:mx-6">
                  <p className="text-xs font-semibold xxs:text-lg xs:text-xl text-center dark-text leading-tight">
                  {isMobile ? 'PIM' : 'PIMs'}
                  </p>

                  <div className="w-full h-1 xs:h-2 mt-1 xxs:mt-1" style={gradientStyle}>
                    <p className="hidden">Gradient line</p>
                  </div>
                  <p className="text-2xl md:text-3xl text-center mt-1 leading-tight">
                    {data.pims}
                  </p>
                </div>
              )}

              {/* MINS */}
              {data.is_netminder && (
                <div className="w-24 xxs:w-28 flex-col justify-center items-center mx-2 xs:mx-3 sm:mx-6">
                  <p className="text-xs font-semibold xxs:text-lg xs:text-xl text-center dark-text leading-tight">
                  {isMobile ? 'MIN' : 'Minutes'}
                  </p>

                  <div className="w-full h-1 xs:h-2 mt-1 xxs:mt-1" style={gradientStyle}>
                    <p className="hidden">Gradient line</p>
                  </div>
                  <p className="text-2xl md:text-3xl text-center mt-1 leading-tight">
                    {data.seconds > 0 ? (String(~~(data.seconds / 60)).padStart(2, '0')) : "-"}
                  </p>
                </div>
              )}

              {/* GA */}
              {data.is_netminder && (
                <div className="w-24 xxs:w-28 flex-col justify-center items-center mx-2 xs:mx-3 sm:mx-6">
                  <p className="text-xs font-semibold xxs:text-lg xs:text-xl text-center dark-text leading-tight">
                  {isMobile ? 'SV%' : 'SAV%'}
                  </p>

                  <div className="w-full h-1 xs:h-2 mt-1 xxs:mt-1" style={gradientStyle}>
                    <p className="hidden">Gradient line</p>
                  </div>
                  <p className="text-2xl md:text-3xl text-center mt-1 leading-tight">
                    {data.sav_pct ? data.sav_pct.toFixed(2) : "-"}
                  </p>
                </div>
              )}

              {/* GAA */}
              {data.is_netminder && (
                <div className="w-24 xxs:w-28 flex-col justify-center items-center mx-2 xs:mx-3 sm:mx-6">
                  <p className="text-xs font-semibold xxs:text-lg xs:text-xl text-center dark-text leading-tight">
                  {isMobile ? 'GAA' : 'GAA'}
                  </p>

                  <div className="w-full h-1 xs:h-2 mt-1 xxs:mt-1" style={gradientStyle}>
                    <p className="hidden">Gradient line</p>
                  </div>
                  <p className="text-2xl md:text-3xl text-center mt-1 leading-tight">
                    {data.gaa ? data.gaa.toFixed(2) : "-"}
                  </p>
                </div>
              )}

              {/* WINS */}
              {data.is_netminder && (
                <div className="w-24 xxs:w-28 flex-col justify-center items-center mx-2 xs:mx-3 sm:mx-6">
                  <p className="text-xs font-semibold xxs:text-lg xs:text-xl text-center dark-text leading-tight">
                  {isMobile ? 'SO' : 'Shutouts'}
                  </p>

                  <div className="w-full h-1 xs:h-2 mt-1 xxs:mt-1" style={gradientStyle}>
                    <p className="hidden">Gradient line</p>
                  </div>
                  <p className="text-2xl md:text-3xl text-center mt-1 leading-tight">
                    {data.shutouts}
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-col xl:flex-row items-start justify-between pt-2">
              <div className="flex flex-col w-full px-2 xxs:px-4 lg:px-8 pb-10">
                <div className="flex flex-col justify-left items-left">
                  {/* Details - Record, Position, First Ga,e */}
                  <DetailsPanel playerId={{ playerId }} />

                  {data.stats.length > 0 ? (
                    <>
                      <h2 className="text-left text-xl font-bold mt-4 mb-2">
                        Statistics
                      </h2>

                      <div className="dark:bg-dark-4 bg-light-2 rounded xs:px-1 py-1 w-full">
                        <table className="table-auto w-full">
                          <thead>
                            <tr>
                              {playerHeaders.map((header) => (
                                <th
                                  key={header}
                                  className="w-20 text-center pb-1  max-xs:text-xs"
                                >
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {data.stats.map((season, index) => (
                              <StatsRow
                                index={index}
                                season={season}
                                nm={false}
                                stats={data.full_stats.filter(
                                  (fullStat) =>
                                    fullStat.season === season.season && fullStat.team_id === season.team_id
                                )}
                                rowContent={
                                  <>
                                    <td className="py-1 text-center max-xs:text-xs">
                                      {season.season}
                                    </td>
                                    <td className="py-1 text-center w-4/12 max-xs:text-xs">
                                      {season.team_name}
                                    </td>
                                    <td className="py-1 text-center max-xs:text-xs">
                                      {season.games}
                                    </td>
                                    <td className="py-1 text-center max-xs:text-xs">
                                      {season.goals}
                                    </td>
                                    <td className="py-1 text-center max-xs:text-xs">
                                      {season.assists}
                                    </td>
                                    <td className="py-1 text-center max-xs:text-xs px-2">
                                      {season.goals + season.assists}
                                    </td>
                                    <td className="py-1 text-center max-xs:text-xs">
                                      {season.pims}
                                    </td>
                                  </>
                                }
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col justify-start items-centre my-5">
                      <h2 className="text-left text-xl font-bold mt-4 mb-2">
                        Statistics
                      </h2>
                      <h2 className="text-left">
                        No Games Found
                      </h2>
                    </div>
                  )}

                  {/* Netminder Statistics */}
                  {data.nm_stats && data.nm_stats.length > 0 && (
                    <>
                      <h2 className="text-left text-xl font-bold mt-3 md:mt-4 mb-2">
                        Netminder Statistics
                      </h2>
                      <div className="dark:bg-dark-4 bg-light-2 rounded xs:px-1 py-1 w-full">
                        {/* Netminder Statistics Table */}
                        <table className="table-auto w-full">
                          <thead>
                            <tr>
                              {goalieHeaders.map((header) => (
                                <th
                                  key={header.name}
                                  className={`text-center pb-1 ${header.extraClasses}`}
                                >
                                  {header.name}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {data.nm_stats.map((season, index) => (
                              <StatsRow
                                index={index}
                                season={season}
                                nm={true}
                                stats={data.full_stats.filter(
                                  (fullStat) =>
                                    fullStat.season === season.season && fullStat.team_id === season.team_id
                                )}
                                rowContent={
                                  <>
                                    <td className="py-1 text-center max-xs:text-xs">
                                      {season.season}
                                    </td>
                                    <td className="py-1 text-center w-4/12 max-xs:text-xs">
                                      {season.team_name}
                                    </td>
                                    <td className="py-1 text-center max-xs:text-xs">
                                      {season.games}
                                    </td>
                                    <td className="max-xs:hidden py-1 text-center max-xs:text-xs">
                                      {(season.seconds / 60).toFixed(0)}
                                    </td>
                                    <td className="max-xs:hidden py-1 text-center max-xs:text-xs">
                                      {season.goals_against}
                                    </td>
                                    <td className="max-xs:hidden py-1 text-center max-xs:text-xs">
                                      {season.shots_against}
                                    </td>
                                    <td className="py-1 text-lef text-center max-xs:text-xs">
                                      {season.sav_pct
                                        ? season.sav_pct.toFixed(2)
                                        : "-"}
                                    </td>
                                    <td className="py-1 text-lef text-center max-xs:text-xs">
                                      {season.gaa ? season.gaa.toFixed(2) : "-"}
                                    </td>
                                    <td className="py-1 text-lef text-center max-xs:text-xs">
                                      {season.shutouts}
                                    </td>
                                  </>
                                }
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {/* Achievements */}
                  <AchievementsPanel playerId={{ playerId }} />
                </div>
              </div>

              {/* Teammates Sidebar */}
              <TeammatesSidebar
                gradientStyle={gradientStyle}
                playerId={playerId}
              />
            </div>
          </div>
        }
      ></Container>
    </>
  ) : (
    <></>
  );
};

export default SinglePlayer;
